import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Box, Card, DialogContent, DialogTitle, Dialog, Typography, Button, IconButton, DialogActions, Divider, Grow, CircularProgress, Avatar, Grid, Stack, /* Tabs, Tab, */ Tooltip, Alert } from '@mui/material';
import { AccessTime, EventBusy, SvgIconComponent} from '@mui/icons-material';
import { getCitas, cancelarCita, Cita } from '../actions/CitasActions';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import Assignment from '@mui/icons-material/Assignment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import moment from 'moment';
type InfoFieldProps = {
  icon: SvgIconComponent;
  label: string;
  value: React.ReactNode;
  compact?: boolean;
};

// Citas que se muestran antes de "Ver todas" (8 = 2 filas completas en pantallas grandes)
const CITAS_INICIALES = 8;

// ══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════
const HomePage: React.FC = () => {
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [loadingCitas, setLoadingCitas] = useState(true);
  const [citas, setCitas] = useState<Cita[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const [errorCancelar, setErrorCancelar] = useState<string | null>(null); // mensaje si falla la cancelación
  const [errorCitas, setErrorCitas] = useState<string | null>(null); // mensaje si falla la carga de citas
  const [verTodas, setVerTodas] = useState(false); // muestra todas las citas o solo las primeras CITAS_INICIALES
  // Pestañas QR / código de barras desactivadas temporalmente: solo se muestra el QR (ver bloque comentado en la card)
  // const [tabsActivas, setTabsActivas] = useState<Record<string, string>>({});
  // const getTab = (id: string, defaultAsistio = false) => tabsActivas[id] ?? (defaultAsistio ? 'qr' : 'barras');
  // const setTab = (id: string, value: string) =>
  //   setTabsActivas(prev => ({ ...prev, [id]: value }));

 // Memorizar el ordenamiento para evitar cálculos innecesarios en cada render
  const citasOrdenadas = useMemo(() => {
    return [...citas].sort((a, b) => Date.parse(a.inicio) - Date.parse(b.inicio));
  }, [citas]);

  // Citas en pantalla: las más próximas primero, todas solo si se pidió "Ver todas"
  const citasVisibles = useMemo(
    () => (verTodas ? citasOrdenadas : citasOrdenadas.slice(0, CITAS_INICIALES)),
    [citasOrdenadas, verTodas]
  );

  // const formatDate = (inicio) => moment(inicio).format("DD/MM/YYYY");
   // Add type annotation for parameter
  const formatDate = (inicio: string | Date) => moment(inicio).format("DD/MM/YYYY");
  const formatTime = (inicio: string | Date): string => moment(inicio).format("HH:mm");

  // Prepara la interfaz para la cancelación: guarda la referencia de la cita seleccionada y muestra el modal de confirmación.
  const handleOpenDialog = (id: string) => {
    setSelectedAppointmentId(id); // Guarda el ID de la cita seleccionada
    setErrorCancelar(null);
    setOpenDialog(true); // Abre el diálogo de confirmación
  };

  // Cierra el diálogo de confirmación y restaura el foco al cuerpo del documento tras la animación para mantener la accesibilidad.
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setErrorCancelar(null);
    setTimeout(() => {
      if (document.body) {
        document.body.focus();
      }
    }, 1000);
  };


// Funcion para manejar la cancelación de la cita
const handleCancelAppointment = async (id: string) => {
  if(!selectedAppointmentId) return; // Asegura que hay una cita seleccionada antes de proceder
  setLoadingConfirm(true); // Indica que se está procesando la cancelación
  setErrorCancelar(null);
  try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('No hay token de autenticación');
      await cancelarCita(id);
      setCitas(prev => prev.filter(cita => cita.id !== id));
      setOpenDialog(false);
      setSelectedAppointmentId(null);
  } catch (error: any) {
      console.error('Error al cancelar la cita:', error);
      setErrorCancelar(error?.message || 'No se pudo cancelar la cita. Intenta de nuevo.');
  }
  setLoadingConfirm(false);
};

// Carga las citas del usuario; se usa al montar y desde el botón "Reintentar"
const cargarCitas = useCallback(async () => {
  setLoadingCitas(true);
  setErrorCitas(null);
  try {
    const res = await getCitas();
    setCitas(res);
  } catch (error) {
    console.error('Error al obtener las citas:', error);
    setErrorCitas('No se pudieron cargar tus citas. Revisa tu conexión e intenta de nuevo.');
  } finally {
    setLoadingCitas(false);
  }
}, []);

useEffect(() => {
  cargarCitas();
}, [cargarCitas]);

if (loadingCitas) {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
      <CircularProgress sx={{ color: '#000' }} />
    </Box>
  );
}

  const esExpediente = (item: Cita) =>
      item.cit_servicio_descripcion?.toLowerCase().includes('expediente') ?? false;


// Componente para campos de información con iconos
const InfoField: React.FC<InfoFieldProps> = ({ icon: Icon, label, value, compact = false }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: compact ? 1.5 : 1.8,
      borderRadius: 2,
      bgcolor: '#f8f9fa',
      border: '1px solid #e9ecef',
      
    }}
  >
    <Avatar sx={{ bgcolor: '#000', width: 36, height: 36 }}>
      <Icon sx={{ fontSize: 18, color: 'white' }} />
    </Avatar>

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          color: '#6c757d',
          fontWeight: 600,
          textTransform: 'uppercase',
          fontSize: '0.65rem',
          letterSpacing: '0.5px',
          display: 'block'
        }}
      >
        {label}
      </Typography>

      {/* IMPORTANTE: evitar error de <p> */}
      <Typography
        variant="body2"
        component="div"
        sx={{
          color: '#000',
          fontWeight: 500,
          fontSize: '0.9rem',
          lineHeight: 1.3,
          wordBreak: 'break-word'
        }}
      >
        {value || 'No especificado'}
      </Typography>
    </Box>
  </Box>
);
return (
  <>
    {/* Contenedor principal */}
    <Container sx={{ pt:14 }} maxWidth="xl">
      {/* Barra de título */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" fontWeight={700} sx={{ color: '#000' }}>
          Mis Citas Agendadas{citasOrdenadas.length > 0 ? ` (${citasOrdenadas.length})` : ''}
        </Typography>
      </Box>

      {/* Grid de citas */}
      <Grid container spacing={2} rowSpacing={4} mb={4} px={4}>
        {errorCitas ? (
          /* Estado de error: no se pudieron cargar las citas */
          <Box width="100%" height={500} textAlign="center" py={6} alignContent={'center'}>
            <Avatar sx={{ bgcolor: '#000', width: 56, height: 56, margin: '0 auto' }}>
              <WarningAmberIcon sx={{ color: 'white' }} />
            </Avatar>
            <Typography variant="h6" mt={2}>
              No se pudieron cargar tus citas
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={2}>
              {errorCitas}
            </Typography>
            <Button variant="contained" onClick={cargarCitas} sx={{ backgroundColor: '#000', '&:hover': { backgroundColor: '#000' } }}>
              Reintentar
            </Button>
          </Box>
        ) : citasOrdenadas.length > 0 ? (
          citasVisibles.map((item: Cita) => {
            // Regla de QR solo con asistencia y código de barras desactivada temporalmente: solo se muestra el QR
            // const asistio = item.estado?.toUpperCase() === 'ASISTIO';
            // const mostrarQr = asistio && !!item.codigo_acceso_url;
            // const mostrarBarras = !!item.codigo_barras_url;
            return (
            <Grow key={item.id} in style={{ transformOrigin: '0 0 0' }} {...({ timeout: 1000 })}>
              <Grid
                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                display="flex"
                justifyContent="center"
                alignItems="flex-start"
              >
                  
                {/* <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}> */}
                {/* <Box justifyItems={'center'} alignItems={'center'}> */}
                  {/* DISEÑO ORIGINAL  CARD RESTAURADO */}
                  <Card
                    sx={{
                      width: '100%',
                      maxWidth: 400,
                      borderRadius: 3,
                      boxShadow: '0 8px 24px rgba(18, 21, 40, 0.12)',
                      overflow: 'hidden',
                      background: 'white',
                    }}
                  >
                    {/* HEADER */}
                    <Box
                      sx={{
                        background: item.estado?.toUpperCase()  === 'ASISTIO'
                        ? 'linear-gradient(135deg,  #1b5e20 0%, #2e7d32 50%, #1b5e20 100%)' 
                        : 'linear-gradient(135deg, #000000 0%, #111111 35%, #1c1c1c 60%, #050505 100%)',
                        color: 'white',
                        p: 2.5,
                        textAlign: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'inline-flex',
                          bgcolor: 'rgba(255,255,255,0.15)',
                          p: 1.2,
                          borderRadius: '50%',
                          mb: 1
                        }}
                      >
                        {item.estado?.toUpperCase()  === 'ASISTIO' 
                          ? <CheckCircleIcon sx={{ fontSize: 28, color: 'white' }} />
                          : <CalendarMonthIcon sx={{ fontSize: 28 }} />
                        }
                      </Box>

                      <Typography variant="h6" sx={{ fontWeight: 400 , textTransform: 'none', letterSpacing: '1px'}}>
                        {/* {item.estado?.toLowerCase()  === 'asistio' ? '' : 'Cita'} */}
                        Cita
                      </Typography>

                      <Typography variant="caption" sx={{ fontWeight: 400 , textTransform: 'none', letterSpacing: '1px'}}>
                        {item.id}
                      </Typography>
                    </Box>

                    {/* CONTENIDO */}
                    <Box 
                      sx={{ 
                        p: 2.5,
                        display: 'flex', 
                        flexDirection: 'column', 
                      }}
                    >
                      <Stack spacing={1.5}>

                        {/* FECHA Y HORA */}
                         <Box
                            sx={{
                              p: 1.5,
                              bgcolor: '#f8f9fa',
                              borderRadius: 2,
                              border: '1px solid #e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 2,     
                            }}
                        >
                            {/* Fecha */}
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1.5 
                              }}
                            >
                                <Box sx={{
                                    display: 'inline-flex',
                                    bgcolor: '#121528',
                                    p: 1,
                                    borderRadius: '50%',
                                    color: 'white'
                                }}>
                                    <CalendarMonthIcon sx={{ fontSize: 20 }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', display: 'block' }}>
                                        Fecha
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#000', fontWeight: 600, fontSize: '0.85rem' }}>
                                        {formatDate(item.inicio)}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Hora */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    display: 'inline-flex',
                                    bgcolor: '#000',
                                    p: 1,
                                    borderRadius: '50%',
                                    color: 'white'
                                }}>
                                    <AccessTime sx={{ fontSize: 20 }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', display: 'block' }}>
                                        Hora
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#121528', fontWeight: 600, fontSize: '0.85rem' }}>
                                        {formatTime(item.inicio)}
                                    </Typography>
                                </Box>
                            </Box>

                        </Box>
                        {/* OFICINA */}
                        <InfoField
                          icon={BusinessIcon}
                          label="Unidad"
                          value={item.oficina_descripcion}
                          compact
                        />

                        {/* SERVICIO */}
                        <InfoField
                          icon={Assignment}
                          label="Tipo de trámite"
                          value={item.cit_servicio_descripcion}
                          compact
                        />

                        {/* NOTAS */}
                        {(() => {
                          const esExp = esExpediente(item);
                          const notas = item.notas ?? '';

                          if (esExp && notas) {
                              // Parsear expedientes con o sin juzgado
                              const entradas = notas.split(/[,]/).map(e => e.trim()).filter(Boolean);
                              const contenido = entradas.map((entry, i, arr) => {
                                  const match = entry.match(/^(.+?)\s*\((.+)\)$/);
                                  const exp = match?.[1]?.trim() ?? entry;
                                  const juzgado = match?.[2]?.trim() ?? '';
                                  return (
                                      <span key={i}>
                                          <strong>{exp}</strong>{juzgado ? ` (${juzgado})` : ''}
                                          {i < arr.length - 1 ? ', ' : ''}
                                      </span>
                                  );
                              });

                              // Para el tooltip también necesitamos el texto completo
                              const textoCompleto = notas;
                              const truncado = textoCompleto.length > 50;

                              return (
                                  <Tooltip title={truncado ? textoCompleto : ''} placement="top" arrow disableHoverListener={!truncado}>
                                      <Box sx={{
                                          display: 'flex', alignItems: 'center', gap: 1.5,
                                          p: 1.5, borderRadius: 2, bgcolor: '#f8f9fa',
                                          border: '1px solid #e9ecef', cursor: truncado ? 'help' : 'default'
                                      }}>
                                          <Avatar sx={{ bgcolor: '#000', width: 36, height: 36 }}>
                                              <DescriptionIcon sx={{ fontSize: 18, color: 'white' }} />
                                          </Avatar>
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                              <Typography variant="caption" sx={{
                                                  color: '#6c757d', fontWeight: 600, textTransform: 'uppercase',
                                                  fontSize: '0.65rem', letterSpacing: '0.5px', display: 'block'
                                              }}>
                                                  Expedientes
                                              </Typography>
                                              <Typography variant="body2" component="div" sx={{
                                                  color: '#000', fontWeight: 500, fontSize: '0.9rem',
                                                  lineHeight: 1.3, wordBreak: 'break-word'
                                              }}>
                                                  {contenido}
                                              </Typography>
                                          </Box>
                                      </Box>
                                  </Tooltip>
                              );
                          }

                          // Notas normales (no expediente)
                          const textoCompleto = notas || 'Sin notas';
                          const truncado = textoCompleto.length > 50;
                          const textoMostrado = truncado ? textoCompleto.slice(0, 50) + '...' : textoCompleto;

                          return (
                              <Tooltip title={truncado ? textoCompleto : ''} placement="top" arrow disableHoverListener={!truncado}>
                                  <Box sx={{
                                      display: 'flex', alignItems: 'center', gap: 1.5,
                                      p: 1.5, borderRadius: 2, bgcolor: '#f8f9fa',
                                      border: '1px solid #e9ecef', cursor: truncado ? 'help' : 'default'
                                  }}>
                                      <Avatar sx={{ bgcolor: '#000', width: 36, height: 36 }}>
                                          <NoteAltIcon sx={{ fontSize: 18, color: 'white' }} />
                                      </Avatar>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography variant="caption" sx={{
                                              color: '#6c757d', fontWeight: 600, textTransform: 'uppercase',
                                              fontSize: '0.65rem', letterSpacing: '0.5px', display: 'block'
                                          }}>
                                              Notas
                                          </Typography>
                                          <Typography variant="body2" component="div" sx={{
                                              color: '#000', fontWeight: 500, fontSize: '0.9rem',
                                              lineHeight: 1.3, wordBreak: 'break-word'
                                          }}>
                                              {textoMostrado}
                                          </Typography>
                                      </Box>
                                  </Box>
                              </Tooltip>
                          );
                      })()}
                       
                      </Stack>

                    {/* Código de barras y pestañas desactivados temporalmente: solo se muestra el QR
                    Solo tabs si tiene ambos,  Tabs solo si hay QR (asistio) y código de barras
                    {mostrarQr && mostrarBarras ? (
                      <Box sx={{ mt: 1, width: '100%' }}>
                        <Tabs
                          value={getTab(item.id)}
                          onChange={(_, v) => setTab(item.id, v)}
                          centered
                          sx={{
                            mb: 2,
                            '& .MuiTab-root': {
                              fontWeight: 600,
                              fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.62rem' },
                              minWidth: 0,
                              px: { xs: 0.5, sm: 1.5 },
                              letterSpacing: 0,
                            },
                            '& .Mui-selected': { color: '#0e0e0eff' },
                            '& .MuiTabs-indicator': { backgroundColor: '#000' },
                          }}
                        >
                          <Tab label="Código asistencia" value="barras" />
                          <Tab label="Código acceso" value="qr" />
                        </Tabs>
                          {getTab(item.id) === 'barras' && (
                            <Box
                              sx={{
                                p: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #dee2e6',
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                justifyContent: 'center', width: '100%', boxSizing: 'border-box',
                              }}
                            >
                              <img
                                alt="barras"
                                src={item.codigo_barras_url}
                                style={{ width: '80%', maxWidth: 200, borderRadius: 8, display: 'block' }}
                              />
                            </Box>
                          )}
                          {getTab(item.id) === 'qr' && (
                            <Box
                              sx={{
                                p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #dee2e6',
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                justifyContent: 'center', width: '100%', boxSizing: 'border-box',
                              }}
                            >
                              <img
                                alt="qr"
                                src={item.codigo_acceso_url}
                                style={{ width: '60%', maxWidth: 200, borderRadius: 8, display: 'block' }}
                              />
                            </Box>
                          )}
                      </Box>
                    ) : mostrarBarras ? (
                      // Solo código de barras — aún no asiste, sin tabs
                      <Box
                        sx={{
                          mt: 2, p: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #dee2e6',
                          display: 'flex', flexDirection: 'column', alignItems: 'center',
                          width: '100%', boxSizing: 'border-box',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: '#6c757d', fontWeight: 600, mb: 1, textTransform: 'uppercase', fontSize: '0.65rem', display: 'block', textAlign: 'center' }}
                        >
                          Código de asistencia
                        </Typography>
                        <img
                          alt="barras"
                          src={item.codigo_barras_url}
                          style={{ width: '80%', maxWidth: 200, borderRadius: 8, display: 'block' }}
                        />
                      </Box>
                    ) : mostrarQr ? (
                      // Solo QR (caso raro: asistió pero no hay código de barras)
                      <Box
                        sx={{
                          mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #dee2e6',
                          display: 'flex', flexDirection: 'column', alignItems: 'center',
                          width: '100%', boxSizing: 'border-box',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: '#6c757d', fontWeight: 600, mb: 1, textTransform: 'uppercase', fontSize: '0.65rem', display: 'block', textAlign: 'center' }}
                        >
                          Código de acceso
                        </Typography>
                        <img
                          alt="qr"
                          src={item.codigo_acceso_url}
                          style={{ width: '60%', maxWidth: 200, borderRadius: 8, display: 'block' }}
                        />
                      </Box>
                    ) : null}
                    */}

                    {/* Solo QR */}
                    {item.codigo_acceso_url && (
                      <Box
                        sx={{
                          mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #dee2e6',
                          display: 'flex', flexDirection: 'column', alignItems: 'center',
                          width: '100%', boxSizing: 'border-box',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: '#6c757d', fontWeight: 600, mb: 1, textTransform: 'uppercase', fontSize: '0.65rem', display: 'block', textAlign: 'center' }}
                        >
                          Código de acceso
                        </Typography>
                        <img
                          alt="qr"
                          src={item.codigo_acceso_url}
                          style={{ width: '60%', maxWidth: 200, borderRadius: 8, display: 'block' }}
                        />
                      </Box>
                    )}

                      {/* BOTÓN */}
                      <Box>
                        {item.puede_cancelarse && (
                          <Button
                            variant="contained"
                            color="error"
                            fullWidth
                            onClick={() => handleOpenDialog(item.id)}
                            sx={{
                              mt: 2,
                              py: 1.2,
                              borderRadius: 2,
                              fontWeight: 600,
                            }}
                          >
                            Cancelar Cita
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Card>
                {/* </Box> */}
              </Grid>
            </Grow>
          )})
        ) : (
          /* Estado vacío: se muestra cuando no hay citas */
          <Box width="100%" height={500} textAlign="center" py={6} alignContent={'center'}>
            <Avatar sx={{ bgcolor: '#000', width: 56, height: 56, margin: '0 auto' }}>
              <CalendarMonthIcon sx={{ color: 'white' }} />
            </Avatar>
            <Typography variant="h6" mt={2}>
              No tienes citas agendadas
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Agenda una nueva cita para comenzar
            </Typography>
          </Box>
        )}
      </Grid>

      {/* Ver todas / ver menos: solo si hay más citas de las que se muestran al inicio */}
      {!errorCitas && citasOrdenadas.length > CITAS_INICIALES && (
        <Box textAlign="center" mb={6}>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Mostrando {citasVisibles.length} de {citasOrdenadas.length} citas
          </Typography>
          <Button
            variant="contained"
            onClick={() => setVerTodas(v => !v)}
            sx={{ bgcolor: '#000', py: 1.5, fontWeight: 700 }}
          >
            {verTodas ? 'Ver menos' : `Ver todas las citas (${citasOrdenadas.length})`}
          </Button>
        </Box>
      )}

      {/* DIÁLOGO DE CONFIRMACIÓN */}
      <Dialog open={openDialog} onClose={loadingConfirm ? undefined : handleCloseDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center">
            <EventBusy sx={{ color: 'dark.main', fontSize: 32, mr: 1.5 }} />
            <Typography variant="h6" fontWeight="bold">Cancelar Cita</Typography>
          </Box>
          <IconButton onClick={handleCloseDialog} disabled={loadingConfirm}><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
            <WarningAmberIcon sx={{ fontSize: 80, color: 'dark.main',  mb: 2 }} />
            <Typography align="center" variant="subtitle1" fontWeight={500}>
              ¿Estás seguro que deseas cancelar tu cita?
            </Typography>
            {errorCancelar && (
              <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                {errorCancelar}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} variant="outlined" color="inherit" disabled={loadingConfirm}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if(selectedAppointmentId) handleCancelAppointment(selectedAppointmentId);
            }}
            variant="contained"
            sx={{ ml: 2, minWidth: 120, backgroundColor: '#000', color: 'white', '&:hover': { backgroundColor: '#000' } }}
            disabled={loadingConfirm}
          >
            {loadingConfirm ? <CircularProgress size={22} color="inherit" /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>   
  </>
)};

export default HomePage;
